<template>
    <el-table
        ref="tableRef"
        :data="tableData"
        row-key="id"
        :tree-props="{ children: 'children' }"
    >
        <el-table-column prop="prop" label="SSID" />
        <el-table-column prop="value" label="">
            <template v-slot="{ row }">
                <div class="colValueInner">
                    <span>{{ row.value }}</span>
                    <!-- パスワード行だけ表示/非表示トグル -->
                    <TransparentButton
                        v-if="row.prop === settingItem.wiFiPassword.label && !isEmptyStr(row.password)"
                        icon-src=""
                        :label="row.visible ? 'HIDE' : 'SHOW'"
                        class="showHideText"
                        @click="toggleIcon(row)"
                    />
                </div>
            </template>
        </el-table-column>

        <el-table-column label="詳細" width="60">
            <template v-slot="{ row }">
                <!-- 第一階層のみ展開アイコン表示 -->
                <i
                    v-if="row.children !== undefined"
                    class="el-icon-caret-left arrowIcon"
                    :class="{ rotated: row.expanded }"
                    @click="toggleRow(row)"
                />
            </template>
        </el-table-column>
    </el-table>
</template>

<script>
import { BC } from '@/components/pc/radio/ipradio/config/BCconstants'
import {
    BC_TRANS_JP,
    FIRST_LEVEL_MENU,
    SECOND_LEVEL_MENU,
    THIRD_LEVEL_MENU,
} from '@/components/pc/radio/ipradio/config/BCconstants.js'
import commonUtil from '@/components/commonutil.js';
import TransparentButton from "./atoms/TransparentButton.vue";

/* ---------- 静的定義（元コードと同じ） ---------- */
// json第一階層
const firstLevel = FIRST_LEVEL_MENU[5].name
// json第二階層
const secondLevel = SECOND_LEVEL_MENU[firstLevel][4].name
// json第三階層
const thirdLevel = THIRD_LEVEL_MENU[firstLevel][secondLevel][0]

const settingItem = {
    ssid: THIRD_LEVEL_MENU[firstLevel][secondLevel][0],
    securityType: thirdLevel.item[0],
    wiFiPassword: thirdLevel.item[1],
    autoConnectSetting: thirdLevel.item[2],
    ipAssignment: thirdLevel.item[3],
    ipAddress: thirdLevel.item[4],
    subnetMask: thirdLevel.item[5],
    defaultGateway: thirdLevel.item[6],
    primaryDNSServer: thirdLevel.item[7],
    secondaryDNSServer: thirdLevel.item[8]
}

const pswdUnset = '[設定なし]'

export default {
    name: 'WifiManagementListVue2',
    components: {TransparentButton},

    props: {
        setting: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            settingItem, // テンプレで参照

            tableData: [],
        }
    },

    created() {
        // 表データ作成
        this.tableData = this.createTableRow()
    },

    methods: {
        // 表データを作成
        createTableRow() {
            const src = (this.setting && this.setting.Network && this.setting.Network['Wi-FiManagementList']) || []
            const copy = JSON.parse(JSON.stringify(src)) // deep copy (structuredClone 代替)
            return copy.map((_setting, d1Index) => {
                const ssid = _setting[settingItem.ssid.name]
                delete _setting[settingItem.ssid.name]

                // セキュリティ EnhancedOpen はパスワード項目を消す＋表示名整形
                if (_setting[settingItem.securityType.name] === BC.NETWORK.WIFI_MANAGEMENT_LIST.SECURITY_TYPE.ENHANCED_OPEN) {
                    delete _setting[settingItem.wiFiPassword.name]
                    _setting[settingItem.securityType.name] =
                        commonUtil.camelToSpacedTitleCase(_setting[settingItem.securityType.name])
                }
                // DHCP の場合は IP 関連を消す
                if (_setting[settingItem.ipAssignment.name] === BC.NETWORK.WIFI_MANAGEMENT_LIST.IP_ASSIGNMENT.DHCP) {
                    delete _setting[settingItem.ipAddress.name]
                    delete _setting[settingItem.subnetMask.name]
                    delete _setting[settingItem.defaultGateway.name]
                    delete _setting[settingItem.primaryDNSServer.name]
                    delete _setting[settingItem.secondaryDNSServer.name]
                }

                return {
                    id: `${d1Index}`,        // ツリー展開管理用
                    prop: ssid,       // 表示名
                    value: '',        // 親行は空
                    expanded: false,  // カスタム矢印の回転用
                    children: Object.entries(_setting).map(([key, value], d2Index) => {
                        const data = {
                            id: `${d1Index}-${d2Index}`,
                            prop: Object.values(settingItem).find(it => it.name === key)?.label || key,
                            value: this.transJP(key, value),
                        }
                        if (key === settingItem.wiFiPassword.name) {
                            data.value = this.isEmptyStr(value) ? pswdUnset : this.hidePassword(value)
                            data.visible = false
                            data.password = value
                        }
                        return data
                    }),
                }
            })
        },

        hidePassword(value) {
            return String(value || '').replace(/./g, '●')
        },

        toggleRow(row) {
            row.expanded = !row.expanded
            // Element UI の tree-table でも toggleRowExpansion が使えます
            this.$refs.tableRef && this.$refs.tableRef.toggleRowExpansion(row)
        },

        toggleIcon(row) {
            row.visible = !row.visible
            if (row.value !== pswdUnset) {
                row.value = row.visible ? row.password : this.hidePassword(row.password)
            }
        },

        isEmptyStr(value) {
            return value === undefined || value === null || value === ''
        },

        transJP(key, value) {
            switch (key) {
                case settingItem.autoConnectSetting.name:
                    return value === BC.COMMON.TOGGLE.ENABLE
                        ? BC_TRANS_JP.COMMON.TOGGLE.ENABLE
                        : BC_TRANS_JP.COMMON.TOGGLE.DISABLE
                case settingItem.ipAssignment.name:
                    return BC_TRANS_JP.NETWORK.WIFI_MANAGEMENT_LIST.IP_ASSIGNMENT[String(value).toUpperCase()]
                default:
                    return value
            }
        },
    },
}
</script>

<style scoped>
/* Element UI のデフォルト展開アイコンを消す（自前の矢印を使う） */
:deep() .el-table__expand-icon { display: none !important; }
:deep() .el-tree-node__expand-icon { display: none; }

.arrowIcon { cursor: pointer; transition: transform .3s ease; }
.arrowIcon.rotated { transform: rotate(-90deg); }

.showHideText :deep(.buttonText) {
    padding: 0;
    font-size: 10px;
    color: #848991;
}

.colValueInner {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

/* 子行のフェードイン */
:deep() .el-table__row--level-1 {
    animation: fade-in .6s cubic-bezier(0.4,0,0.2,1);
}

@keyframes fade-in {
    from { opacity: 0; transform: translateY(-5px); }
    to   { opacity: 1; transform: translateY(0); }
}
</style>
